import { ref } from 'vue';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useInternational } from '@/common/locale';
import { useMessage } from '@/common/inject/useMessage';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import type { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import type {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import type { PanelInfo } from '@/common/stores/slide-detail.logic';
import { UTC_TIME_FORMAT } from '@/common/utils/core/time/constants';

/*
 *  URL Monitoring 은 triggerPath 는 있는데 연계는 안하고 있는 것 같음
 *  [참고] detailTabPanelInfo[triggerPath]
 * */

type UrlMonitoringAdditionBase = NonNullable<
  PanelInfo<typeof TOTAL_DETAIL.URL_MONITORING>['addition']
>;

type UrlMonitoringAddition = Omit<UrlMonitoringAdditionBase, 'url'> & {
  url: string;
};

export type UrlMonitoringAdapterInput = UrlMonitoringAddition & {
  targetId: string;
  targetName: string;
};

type NormalizedParams = Partial<UrlMonitoringAddition> &
  Pick<UrlMonitoringAddition, 'applicationId' | 'url'>;

const normalizedParamsSchema = z.custom<Partial<UrlMonitoringAdapterInput>>().transform(
  (input): NormalizedParams => ({
    ...input,
    applicationId: input.applicationId || input.targetId || '',
    url: input.url || input.targetName || '',
  }),
);

const MAX_PERIOD_DAYS = 31;

const urlMonitoringParamsSchema = z
  .object({
    applicationId: z.string().min(1),
    url: z.string().optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
  })
  .transform((data) => {
    if (data.fromTime && data.toTime) {
      const from = dayjs(data.fromTime);
      const to = dayjs(data.toTime);

      if (!from.isValid() || !to.isValid() || to.diff(from) <= 0) {
        return { ...data, fromTime: undefined, toTime: undefined };
      }

      //NOTE: 최대 31일까지 조회 가능
      if (to.diff(from, 'day', true) > MAX_PERIOD_DAYS) {
        return { ...data, fromTime: to.subtract(MAX_PERIOD_DAYS, 'day').format(UTC_TIME_FORMAT) };
      }
    }
    return data;
  });

const getMissingParams = (error: z.ZodError) =>
  error.issues
    .map((issue) => issue.path[0])
    .filter((path): path is string => typeof path === 'string')
    .join(', ');

export const useUrlMonitoringAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  const validateUrlMonitoringDetail = async (
    params: Partial<UrlMonitoringAdapterInput>,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    parsedParams?: z.infer<typeof urlMonitoringParamsSchema>;
  }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;
    const normalizedParams = normalizedParamsSchema.parse(params);

    // 1. 권한 체크
    if (checkPermission) {
      const denied = isDetailSlidePermissionDenied({
        type: TOTAL_DETAIL.URL_MONITORING,
        rolePermissionKey:
          rolePermissionKey || ROLE_PERMISSION_KEY.MONITORING.MONITORING_APPLICATION_URL_MONITORING,
        showError: options.showErrorMessage,
      });
      if (denied) return { reason: 'permission_denied' };
    }

    // 2. 타겟 유효성 검증
    if (checkTarget && normalizedParams.applicationId) {
      const targetValidation = await checkIsValidTarget(
        {
          targetId: normalizedParams.applicationId,
          targetKind: TargetStatusRequestTargetKind.URL,
        },
        {
          showMessageIfTargetNil: options.showErrorMessage,
          showErrorMessage: options.showErrorMessage,
        },
      );
      if (!targetValidation.valid) return { reason: targetValidation.reason };
    }

    // 3. 대상 정보 조회 및 필수값 검증
    if (checkParams) {
      const parsed = urlMonitoringParamsSchema.safeParse(normalizedParams);
      if (!parsed.success) {
        const errorMsg = t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
          value: getMissingParams(parsed.error),
        });
        if (options.showErrorMessage) showErrorMsg({ msgStr: errorMsg });
        return { reason: 'invalid_params' };
      }
      return { reason: 'ok', parsedParams: parsed.data };
    }

    return { reason: 'ok' };
  };

  const openUrlMonitoringDetail = async ({
    params,
    options,
    selectedTab,
    state,
    currentState,
    init,
  }: SlideDetailAdapterParams<
    Partial<UrlMonitoringAdapterInput>
  >): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
    } = options ?? {};

    const { reason, parsedParams } = await validateUrlMonitoringDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !parsedParams) return createSlideDetailResult(reason);
    const validResult = parsedParams;

    // 4. 탭 패널 열기
    const result = addTabPanel(
      {
        type: TOTAL_DETAIL.URL_MONITORING,
        name: validResult.url ?? '',
        addition: {
          applicationId: validResult.applicationId,
          fromTime: validResult.fromTime,
          toTime: validResult.toTime,
        },
        state: { ...state, selectedTab },
        init,
      },
      currentState,
    );

    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return { isSlideDetailOpened, openUrlMonitoringDetail };
};
